import React, { createContext, useCallback, useContext, useState } from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { generateId } from '../utils/id';

const ToastContext = createContext(null);
const MAX_TOASTS = 3;

function normalizeMessage(message) {
  if (message == null) return '';
  return String(message).trim();
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', duration = 4000) => {
    try {
      const text = normalizeMessage(message);
      if (!text) return null;

      const id = generateId();
      setToasts((t) => [...t.slice(-(MAX_TOASTS - 1)), { id, message: text, type }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    } catch (e) {
      console.error('Toast failed:', e);
      return null;
    }
  }, [dismiss]);

  const success = useCallback(
    (msg) => toast(msg || 'Done', 'success'),
    [toast]
  );
  const error = useCallback(
    (msg) => toast(msg || 'Something went wrong', 'error', 6000),
    [toast]
  );
  const info = useCallback((msg) => toast(msg, 'info'), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, info, dismiss }}>
      {children}
      <div
        className="pointer-events-none fixed z-[100] inset-x-0 bottom-[calc(4.75rem+var(--app-safe-bottom))] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:items-end lg:bottom-8"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} {...t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ message, type, onDismiss }) {
  const styles = {
    success: 'border-success/25 bg-card text-text shadow-lg',
    error: 'border-danger/25 bg-card text-text shadow-lg',
    info: 'border-primary/20 bg-card text-text shadow-lg',
  };
  const iconStyles = {
    success: 'text-success',
    error: 'text-danger',
    info: 'text-primary',
  };
  const Icon = type === 'success' ? CheckCircle2 : type === 'error' ? AlertCircle : Info;

  return (
    <div
      className={`pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border px-4 py-3 ${styles[type] || styles.info}`}
      role="alert"
    >
      <Icon className={`h-5 w-5 shrink-0 ${iconStyles[type] || iconStyles.info}`} />
      <p className="flex-1 text-sm font-medium leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-md p-1 text-muted hover:bg-surface hover:text-text"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
